import itemFixtures from '../../fixtures/items.json';
import partFixtures from '../../fixtures/parts.json';
import siteFixtures from '../../fixtures/sites.json';
import type {
  DeviceMessage,
  DeviceNotice,
  DeviceSituation,
  NotificationEmail,
  NotificationRun,
  NotificationSettings,
  NotificationSettingsList,
  NotificationSnooze,
  PenActivity,
  PenActivityMonth,
  SettingsPatch,
  SnoozeInput,
} from '../domain/notification';
import { ConflictError, NotFoundError, RunTooSoonError, ValidationError } from './errors';
import type { NotificationsApi } from './notifications-api';

const wait = async (milliseconds = 60): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const clone = <T>(value: T): T => structuredClone(value);

const DAY = 24 * 60 * 60 * 1000;
const MOWI = 'a4ba0f08-0656-4c97-b301-904d6e386c03';

interface FixtureItem {
  id: string;
  partId: string;
  siteId: string;
  locationSiteId?: string;
  status?: string;
}
interface FixtureSite {
  id: string;
  customerId: string;
  name: string;
}

const HEARTBEAT_TYPES = new Set(['CAMERA_SYSTEM', 'WINCH_SYSTEM', 'UNIT_CONTROLLER', 'POWERBOX']);

const sites = new Map((siteFixtures as FixtureSite[]).map((s) => [s.id, s]));
const partTypes = new Map(
  (partFixtures as { id: string; type: string }[]).map((p) => [p.id, p.type]),
);

/**
 * Twelve of Mowi's fixture devices, each given a fixed situation: cameras,
 * and a winch as the tenth.
 */
const SCENARIOS: { situation: DeviceSituation; message: DeviceMessage; days: number }[] = [
  { situation: 'QUIET', message: 'PLEASE_RETURN', days: 81 },
  { situation: 'QUIET', message: 'CHECK_OFFLINE', days: 30 },
  { situation: 'NOT_INSTALLED', message: 'PLEASE_INSTALL', days: 45 },
  { situation: 'QUIET', message: 'PLEASE_RETURN', days: 22 },
  { situation: 'NOT_INSTALLED', message: 'PLEASE_INSTALL', days: 106 },
  { situation: 'QUIET', message: 'INTERNAL_ONLY', days: 33 },
  { situation: 'QUIET', message: 'CHECK_OFFLINE', days: 57 },
  { situation: 'NOT_INSTALLED', message: 'PLEASE_INSTALL', days: 25 },
  { situation: 'QUIET', message: 'PLEASE_RETURN', days: 64 },
  { situation: 'QUIET', message: 'CHECK_OFFLINE', days: 40 },
  { situation: 'NOT_INSTALLED', message: 'PLEASE_INSTALL', days: 70 },
  { situation: 'QUIET', message: 'PLEASE_RETURN', days: 28 },
];

const atMowi = (type: string) =>
  (itemFixtures as FixtureItem[]).filter((i) => {
    const site = sites.get(i.locationSiteId ?? i.siteId);
    return (
      partTypes.get(i.partId) === type &&
      site?.customerId === MOWI &&
      (i.status === 'ACTIVE' || i.status === 'DELIVERED_TO_CUSTOMER')
    );
  });
const fixtureCameras = atMowi('CAMERA_SYSTEM');
const cameras = [
  ...fixtureCameras.slice(0, 9),
  atMowi('WINCH_SYSTEM')[0]!,
  ...fixtureCameras.slice(9, SCENARIOS.length - 1),
];

const builtInDefaults: NotificationSettings = {
  id: 'DEVICE_SUMMARY',
  quietAfterDays: 21,
  neverInstalledAfterDays: 21,
  reminderAfterDays: [35, 49],
  escalateAfterDays: 49,
  digestIntervalDays: 7,
  snoozeMaxDays: 90,
  signalWindowDays: 14,
  delivery: 'INTERNAL',
  version: 0,
};

/**
 * In-memory device summary for tests and the default (mock) mode: twelve
 * fixture cameras, one already run. A camera with open field work (the
 * fourth) is left out; a snoozed one is left out on the next run.
 */
export class MockNotificationsApi implements NotificationsApi {
  private defaults: NotificationSettings = clone(builtInDefaults);
  private overrides = new Map<string, NotificationSettings>();
  private runs: NotificationRun[] = [];
  private notices: DeviceNotice[] = [];
  private snoozes: NotificationSnooze[] = [];
  private emails: NotificationEmail[] = [];
  private counter = 0;

  constructor(private readonly now: () => number = () => Date.now()) {
    this.run(this.now() - 2 * DAY);
  }

  async listSettings(): Promise<NotificationSettingsList> {
    await wait();
    return clone({ defaults: this.defaults, overrides: [...this.overrides.values()] });
  }

  async updateSettings(patch: SettingsPatch): Promise<NotificationSettings> {
    await wait(100);
    const current = patch.customerId
      ? (this.overrides.get(patch.customerId) ?? {
          customerId: patch.customerId,
          reminderAfterDays: [],
          version: 0,
        })
      : this.defaults;
    if (current.version !== patch.version) {
      throw new ConflictError('These settings were changed by someone else; reload and try again');
    }
    if (!patch.customerId && patch.clear.some((p) => p !== 'watch_only_until'))
      throw new ValidationError('the defaults can’t be cleared, only changed');
    const next: NotificationSettings = {
      ...current,
      ...clone(patch.values),
      version: current.version + 1,
    };
    const byProto: Record<string, keyof NotificationSettings> = {
      quiet_after_days: 'quietAfterDays',
      never_installed_after_days: 'neverInstalledAfterDays',
      escalate_after_days: 'escalateAfterDays',
      digest_interval_days: 'digestIntervalDays',
      snooze_max_days: 'snoozeMaxDays',
      signal_window_days: 'signalWindowDays',
      delivery: 'delivery',
      watch_only_until: 'watchOnlyUntil',
    };
    for (const path of patch.clear) {
      if (path === 'reminder_after_days') next.reminderAfterDays = [];
      else delete next[byProto[path] as 'quietAfterDays'];
    }
    if (patch.customerId) this.overrides.set(patch.customerId, next);
    else this.defaults = next;
    return clone(next);
  }

  async runDeviceSummary(force = false): Promise<NotificationRun> {
    await wait(150);
    const last = this.runs.at(-1);
    const interval = (this.defaults.digestIntervalDays ?? 7) * DAY;
    if (
      !force &&
      last?.createTime &&
      this.now() - Date.parse(last.createTime) < interval - 60 * 60 * 1000
    ) {
      throw new RunTooSoonError(
        `the last run was on ${last.createTime.slice(0, 16).replace('T', ' ')}; set force to run again`,
      );
    }
    return clone(this.run(this.now()));
  }

  async listRuns(): Promise<NotificationRun[]> {
    await wait();
    return clone([...this.runs].reverse());
  }

  async listNotices(runId?: string): Promise<{ run?: NotificationRun; notices: DeviceNotice[] }> {
    await wait();
    const run = runId ? this.runs.find((r) => r.id === runId) : this.runs.at(-1);
    if (!run) {
      if (runId) throw new NotFoundError(`notification run ${runId} not found`);
      return { notices: [] };
    }
    return clone({ run, notices: this.notices.filter((n) => n.runId === run.id) });
  }

  async listEmails(runId: string): Promise<NotificationEmail[]> {
    await wait();
    return clone(this.emails.filter((e) => e.runId === runId));
  }

  async createSnooze(input: SnoozeInput): Promise<NotificationSnooze> {
    await wait(100);
    const reason = input.reason.trim();
    if (!reason) throw new ValidationError('a reason is required');
    const until = Date.parse(input.until);
    const max = (this.defaults.snoozeMaxDays ?? 90) * DAY;
    if (!(until > this.now()) || until > this.now() + max) {
      throw new ValidationError(
        `until must be in the next ${this.defaults.snoozeMaxDays ?? 90} days`,
      );
    }
    const camera = cameras.find((c) => c.id === input.itemId);
    if (!camera) throw new NotFoundError(`item ${input.itemId} not found`);
    const site = sites.get(camera.locationSiteId ?? camera.siteId);
    const snooze: NotificationSnooze = {
      id: `snooze-${(this.counter += 1)}`,
      itemId: camera.id,
      customerId: site?.customerId,
      siteId: site?.id,
      until: new Date(until).toISOString(),
      reason,
      createdBy: 'mockuser@tidalx.ai',
      createTime: new Date(this.now()).toISOString(),
    };
    this.snoozes.push(snooze);
    return clone(snooze);
  }

  async listSnoozes(includeExpired = false): Promise<NotificationSnooze[]> {
    await wait();
    return clone(this.snoozes.filter((s) => includeExpired || Date.parse(s.until) > this.now()));
  }

  async deleteSnooze(id: string): Promise<void> {
    await wait();
    if (!this.snoozes.some((s) => s.id === id)) throw new NotFoundError(`snooze ${id} not found`);
    this.snoozes = this.snoozes.filter((s) => s.id !== id);
  }

  private run(at: number): NotificationRun {
    const id = `run-${(this.counter += 1)}`;
    const createTime = new Date(at).toISOString();
    // Like the server: emails go out once the defaults' watch-only date has
    // passed, and then only emailed cameras count as told.
    const sending = Boolean(
      this.defaults.watchOnlyUntil && Date.parse(this.defaults.watchOnlyUntil) <= at,
    );
    const previous = new Map(
      this.notices
        .filter((n) => n.decision === 'SENT' || (!sending && n.decision === 'WOULD_SEND'))
        .map((n) => [n.itemId, n]),
    );
    const first = new Map(
      this.notices
        .filter((n) => n.decision === 'SENT' || n.decision === 'WOULD_SEND')
        .map((n) => [n.itemId, n.firstIncluded]),
    );
    let included = 0;
    let skipped = 0;
    let notDue = 0;
    cameras.forEach((camera, index) => {
      const scenario = SCENARIOS[index]!;
      const site = sites.get(camera.locationSiteId ?? camera.siteId)!;
      const snooze = this.snoozes.find((s) => s.itemId === camera.id && Date.parse(s.until) > at);
      const before = previous.get(camera.id);
      const notice: DeviceNotice = {
        id: `${id}-${camera.id}`,
        runId: id,
        itemId: camera.id,
        customerId: site.customerId,
        siteId: site.id,
        situation: scenario.situation,
        message: scenario.message,
        daysWaiting: scenario.days + Math.round((at - this.now()) / DAY),
        ...(scenario.situation === 'QUIET' ? { penId: `M${index + 1}` } : {}),
        escalationLevel: 0,
        // The last camera's customer has no Customer Success owner yet.
        recipients: index === cameras.length - 1 ? [] : ['cs-owner@tidalx.ai'],
        customerRecipients: index % 5 === 4 ? [] : [`site-${site.name.toLowerCase()}@example.com`],
        language: 'nb',
        createTime,
        deviceType: partTypes.get(camera.partId) ?? 'CAMERA_SYSTEM',
        firstIncluded: first.get(camera.id) ?? createTime,
      };
      if (index === 3) {
        notice.decision = 'SKIPPED';
        notice.skipReason = 'Open field work at the site: Service visit';
        skipped += 1;
      } else if (snooze) {
        notice.decision = 'SKIPPED';
        notice.skipReason = `Snoozed until ${snooze.until.slice(0, 10)}: ${snooze.reason}`;
        skipped += 1;
      } else if (before) {
        notice.decision = 'NOT_DUE';
        notDue += 1;
      } else {
        notice.decision = sending && notice.recipients.length > 0 ? 'SENT' : 'WOULD_SEND';
        included += 1;
      }
      this.notices.push(notice);
    });
    const byRecipient = new Map<string, string[]>();
    for (const n of this.notices) {
      if (n.runId !== id || n.decision !== 'SENT') continue;
      for (const r of n.recipients) byRecipient.set(r, [...(byRecipient.get(r) ?? []), n.id]);
    }
    for (const [recipient, noticeIds] of byRecipient) {
      this.emails.push({
        id: `email-${(this.counter += 1)}`,
        runId: id,
        recipient,
        subject: `Device summary for Mowi: ${noticeIds.length} devices`,
        noticeIds,
        status: 'SENT',
        createTime,
      });
    }
    const run: NotificationRun = {
      id,
      createTime,
      startedBy: 'mockuser@tidalx.ai',
      watchOnly: !sending,
      devices: cameras.length,
      included,
      skipped,
      notDue,
      emailsSent: byRecipient.size,
      emailsFailed: 0,
    };
    this.runs.push(run);
    return run;
  }

  /**
   * Active heartbeat devices report from a pen picked from their id, so
   * cameras at one site sometimes share a pen, as a replacement does. About a
   * quarter of them stay silent all month.
   */
  async listPenActivity(month?: string): Promise<PenActivityMonth> {
    await wait();
    const today = new Date().toISOString().slice(0, 10);
    const current = today.slice(0, 7);
    const target = month ?? current;
    if (!/^\d{4}-\d{2}$/.test(target)) throw new ValidationError(`month "${target}" isn't YYYY-MM`);
    if (target > current) throw new ValidationError(`month ${target} hasn't started`);
    const lastDay =
      target === current
        ? today
        : new Date(Date.UTC(Number(target.slice(0, 4)), Number(target.slice(5, 7)), 0))
            .toISOString()
            .slice(0, 10);
    const span = Number(lastDay.slice(8, 10));
    const activity: PenActivity[] = [];
    for (const item of itemFixtures as FixtureItem[]) {
      const type = partTypes.get(item.partId) ?? '';
      if (item.status !== 'ACTIVE' || !HEARTBEAT_TYPES.has(type)) continue;
      const hash = [...item.id].reduce((sum, char) => sum + char.charCodeAt(0), 0);
      if (hash % 4 === 0) continue;
      const start = 1 + (hash % Math.max(1, Math.min(span, 10)));
      activity.push({
        deviceId: item.id.toUpperCase(),
        siteId: item.locationSiteId ?? item.siteId,
        pen: `M${(hash % 9) + 1}`,
        firstDay: `${target}-${String(start).padStart(2, '0')}`,
        lastDay,
        days: span - start + 1,
      });
    }
    return { month: target, throughDay: activity.length ? lastDay : undefined, activity };
  }
}

export const notificationsApi = new MockNotificationsApi();
